<script setup>
/**
 * @fileoverview AccordionSection component for Custom Mosaics.
 *
 * A collapsible section with a header button that toggles content visibility.
 * Supports both controlled (via v-model) and uncontrolled modes.
 *
 * Features:
 * - Animated chevron indicator for open/closed state
 * - Optional subtitle display
 * - Configurable expand behavior (fill available space vs. content-driven height)
 * - Accessible with aria-expanded attribute
 *
 * @component
 */
import { ref, computed } from 'vue';

/**
 * Component props.
 * @property {string} title - The accordion header title (can be overridden via slot)
 * @property {string} [subtitle] - Optional subtitle displayed next to the title
 * @property {boolean} [open] - Controlled open state (undefined for uncontrolled mode)
 * @property {boolean} [startOpen] - Initial open state for uncontrolled mode
 * @property {boolean} [expandOnOpen] - When true, open section fills available space
 */
const props = defineProps({
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    open: { type: Boolean, default: undefined },
    startOpen: { type: Boolean, default: true },
    expandOnOpen: { type: Boolean, default: true },
});

const emit = defineEmits(['update:open']);

/** @type {import('vue').Ref<boolean>} Internal open state for uncontrolled mode */
const internalOpen = ref(!!props.startOpen);

/**
 * Whether the component is in controlled mode (open prop is defined).
 * @type {import('vue').ComputedRef<boolean>}
 */
const isControlled = computed(() => props.open !== undefined);

/**
 * Current open state, handling both controlled and uncontrolled modes.
 * @type {import('vue').WritableComputedRef<boolean>}
 */
const isOpen = computed({
    get() {
        return isControlled.value ? props.open : internalOpen.value;
    },
    set(v) {
        if (isControlled.value) emit('update:open', v);
        else internalOpen.value = v;
    },
});

/**
 * Toggle the accordion open/closed state.
 */
function toggle() {
    isOpen.value = !isOpen.value;
}
</script>

<template>
    <section
        class="border-b border-engobe-300 mb-4 flex flex-col min-h-0"
        :class="isOpen && props.expandOnOpen ? 'flex-1' : ''"
    >
        <button
            type="button"
            class="w-full flex items-center justify-between gap-2 px-4 py-3 bg-dryline text-white"
            :aria-expanded="isOpen ? 'true' : 'false'"
            @click="toggle"
        >
            <div class="flex items-center gap-3">
                <span class="font-body text-lg tracking-tight leading-relaxed text-white">
                    <slot name="title">{{ title }}</slot>
                </span>
                <span v-if="subtitle" class="text-engobe-200 text-xs">
                    {{ subtitle }}
                </span>
            </div>
            <svg
                class="w-4 h-4 transition-transform"
                :class="isOpen ? 'rotate-180' : ''"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
            >
                <path
                    fill-rule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                    clip-rule="evenodd"
                />
            </svg>
        </button>

        <div
            v-show="isOpen"
            :class="props.expandOnOpen ? 'min-h-0 flex-1 overflow-auto bg-white' : 'bg-white'"
        >
            <slot />
        </div>
    </section>
</template>
